import React, { useEffect } from 'react';
import { Shield, Award, Users, Target, Phone, Mail, MapPin } from 'lucide-react';

const AboutPage = () => {
  useEffect(() => {
    // Force scroll to top immediately
    window.scrollTo(0, 0);
    
    // Remove any hash from URL (for search engine results)
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
    
    // Additional safety check after a short delay
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <div className="relative h-96 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1518136247453-74e7b5265980?w=1920&q=80"
          alt="About Indya Bungy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/56 to-transparent"></div>
        <div className="absolute inset-0 flex items-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto w-full">
            <h1 className="text-6xl md:text-8xl font-black text-white uppercase tracking-tight mb-4">
              About Us
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl">
              India's Most Trusted Bungee Jumping Operator
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-20 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Story Section */}
          <div className="mb-20">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-black text-white mb-6 text-center">
                Our <span className="text-yellow-400">Story</span>
              </h2>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  Indya Bungy is India's leading bungee jumping operator with more than a decade of experience in delivering safe, unforgettable adventure experiences. Over the years, we have completed 44,000+ jumps, earning the trust of thrill-seekers across the country.
                </p>
                <p>
                  Operated under our parent company, Flysports Professional India Pvt. Ltd., we began with a simple vision — to introduce world-class, safe and accessible adventure sports in India. Today, that vision has grown into multiple operational sites nationwide, each designed to offer a unique blend of excitement and safety.
                </p>
                <p>
                  We proudly follow SANZ (Standards of Australia and New Zealand) protocols for all our operations and hold recognition from the Ministry of Tourism. Every jump is overseen by certified experts, ensuring a professional, reliable and truly exhilarating experience.
                </p>
                <p>
                  At Indya Bungy, safety isn't just a promise — it's our foundation.
                </p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20">
            <div className="text-center">
              <div className="text-5xl font-black text-yellow-400 mb-2">44K+</div>
              <div className="text-gray-400 font-medium">Successful Jumps</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black text-yellow-400 mb-2">10+</div>
              <div className="text-gray-400 font-medium">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black text-yellow-400 mb-2">5</div>
              <div className="text-gray-400 font-medium">Star Rating</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black text-yellow-400 mb-2">100%</div>
              <div className="text-gray-400 font-medium">Safety Record</div>
            </div>
          </div>

          {/* Values */}
          <h2 className="text-4xl font-black text-white text-center mb-16">
            Our <span className="text-yellow-400">Values</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center hover:border-yellow-400/40 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-4">
                <Shield className="text-yellow-400" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Safety First</h3>
              <p className="text-gray-400 text-sm">
                We follow international safety standards and use premium certified equipment for every jump.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center hover:border-yellow-400/40 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-4">
                <Award className="text-yellow-400" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Excellence</h3>
              <p className="text-gray-400 text-sm">
                We strive for excellence in every aspect of our service, from equipment to customer experience.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center hover:border-yellow-400/40 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-4">
                <Users className="text-yellow-400" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Community</h3>
              <p className="text-gray-400 text-sm">
                We build a community of thrill-seekers and adventure enthusiasts across India.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center hover:border-yellow-400/40 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-4">
                <Target className="text-yellow-400" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Innovation</h3>
              <p className="text-gray-400 text-sm">
                We continuously innovate to provide the best adventure experiences in India.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information Section */}
      <div className="py-20 bg-gradient-to-b from-gray-950 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Phone & WhatsApp Card */}
            <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-400/20 rounded-2xl p-8 text-center hover:border-yellow-400/50 transition-all duration-300">
              <div className="w-20 h-20 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-6">
                <Phone className="text-yellow-400" size={40} />
              </div>
              <h3 className="text-2xl font-black text-white mb-4">Call Us</h3>
              <a href="tel:8506865393" className="block text-yellow-400 hover:text-yellow-300 text-xl mb-2 transition-colors">
                8506865393
              </a>
              <p className="text-gray-400 text-sm mb-4">Available 24/7</p>
              <a 
                href="https://wa.me/918506865393" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-full transition-colors"
              >
                WhatsApp Us
              </a>
            </div>

            {/* Email Card */}
            <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-400/20 rounded-2xl p-8 text-center hover:border-yellow-400/50 transition-all duration-300">
              <div className="w-20 h-20 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-6">
                <Mail className="text-yellow-400" size={40} />
              </div>
              <h3 className="text-2xl font-black text-white mb-4">Email Us</h3>
              <a href="mailto:info@indyabungy.com" className="block text-yellow-400 hover:text-yellow-300 text-lg mb-2 transition-colors break-words">
                info@indyabungy.com
              </a>
              <p className="text-gray-400 text-sm">We'll respond within 24 hours</p>
            </div>

            {/* Address Card */}
            <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-400/20 rounded-2xl p-8 text-center hover:border-yellow-400/50 transition-all duration-300">
              <div className="w-20 h-20 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-6">
                <MapPin className="text-yellow-400" size={40} />
              </div>
              <h3 className="text-2xl font-black text-white mb-4">Visit Us</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Indya Bungy Headquarters<br />
                Goa, India
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Social Media Section */}
      <div className="py-20 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-4">
            OUR SOCIAL MEDIA <span className="text-yellow-400">HANDLES</span>
          </h2>
          <p className="text-xl text-gray-400 text-center mb-12">
            Follow us for daily updates and adventures
          </p>
          
          <div className="flex items-center justify-center space-x-8">
            <a 
              href="https://www.snapchat.com/add/indya.bungy?share_id=R69eXHq-EQw&locale=en-IN" 
              target="_blank" 
              rel="noopener noreferrer"
              className="transform hover:scale-110 transition-transform duration-300"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_jump-fix/artifacts/iuh9o4eb_S1.png"
                alt="Snapchat"
                className="w-16 h-16 object-contain"
              />
            </a>
            <a 
              href="https://www.instagram.com/indyabungy/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="transform hover:scale-110 transition-transform duration-300"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_jump-fix/artifacts/te2glmk7_S2.png"
                alt="Instagram"
                className="w-16 h-16 object-contain"
              />
            </a>
            <a 
              href="https://www.linkedin.com/in/siddharth-pareek-872070153/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="transform hover:scale-110 transition-transform duration-300"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_jump-fix/artifacts/8etv5x0q_S3.png"
                alt="LinkedIn"
                className="w-16 h-16 object-contain"
              />
            </a>
            <a 
              href="https://www.facebook.com/people/Indya-Bungy/61569196414294/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="transform hover:scale-110 transition-transform duration-300"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_jump-fix/artifacts/55il97x6_S4.png"
                alt="Facebook"
                className="w-16 h-16 object-contain"
              />
            </a>
            <a 
              href="https://www.youtube.com/@INDYABUNGY" 
              target="_blank" 
              rel="noopener noreferrer"
              className="transform hover:scale-110 transition-transform duration-300"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_jump-fix/artifacts/g5ng96xm_S5.png"
                alt="LinkedIn"
                className="w-16 h-16 object-contain"
              />
            </a>
          </div>
        </div>
      </div>

      {/* Find Us on Google Section */}
      <div className="py-20 bg-gradient-to-b from-gray-950 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-4">
            FIND US ON <span className="text-yellow-400">GOOGLE</span>
          </h2>
          <p className="text-xl text-gray-400 text-center mb-12">
            Check out our reviews and ratings
          </p>
          
          <div className="flex justify-center">
            <a
              href="https://www.google.com/search?q=indya+bungy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-3 bg-yellow-400 hover:bg-yellow-300 text-black px-8 py-4 rounded-full font-black text-lg transform hover:scale-105 transition-all duration-300 shadow-lg shadow-yellow-400/50"
            >
              <span>FIND US ON GOOGLE</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;